import { Fragment, useState, useRef } from "react";
import { useCropTypesPaged, useDeleteCropType } from "../hooks/useCropTypes";
import { useAllCropVarieties, useDeleteCropVariety } from "../hooks/useCropVarieties";
import { CreateCropTypeDialog } from "../components/cropTypes/CreateCropTypeDialog";
import { EditCropTypeDialog } from "../components/cropTypes/EditCropTypeDialog";
import { CreateCropVarietyDialog } from "../components/cropVarieties/CreateCropVarietyDialog";
import { EditCropVarietyDialog } from "../components/cropVarieties/EditCropVarietyDialog";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronRight, ChevronDown, MoreHorizontal, Trash2, Search } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { Input } from "@/components/ui/input";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useConfirm } from "@/shared/contexts/confirm-context";

export const CropsManagement = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, error } = useCropTypesPaged({ pageNumber, pageSize, search: search || undefined });
  const { data: allVarieties } = useAllCropVarieties();
  const deleteCropType = useDeleteCropType();
  const deleteCropVariety = useDeleteCropVariety();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPageNumber(1);
    }, 300);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  if (isLoading && !data) return <LoadingAnimation fullScreen />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load crops</p>
      </div>
    );
  }

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const varietiesFor = (cropTypeId: string) =>
    (allVarieties ?? []).filter((v) => v.cropTypeId === cropTypeId);

  const cropTypes = data?.items ?? [];

  const handleDeleteCropType = (id: string, name: string) => {
    confirm({
      title: "Delete crop type?",
      description: `“${name}” will be permanently removed. This cannot be undone.`,
      variant: "destructive",
      confirmLabel: "Delete",
      onConfirm: () =>
        deleteCropType
          .mutateAsync(id)
          .then(() => notify.success(`"${name}" deleted`))
          .catch((e) => {
            notify.error(getErrorMessage(e));
            throw e;
          }),
    });
  };

  const handleDeleteVariety = (id: string, name: string) => {
    confirm({
      title: "Delete variety?",
      description: `“${name}” will be permanently removed. This cannot be undone.`,
      variant: "destructive",
      confirmLabel: "Delete",
      onConfirm: () =>
        deleteCropVariety
          .mutateAsync(id)
          .then(() => notify.success(`"${name}" deleted`))
          .catch((e) => {
            notify.error(getErrorMessage(e));
            throw e;
          }),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Crops &amp; Varieties</h1>
          <p className="text-gray-500 mt-1">Manage crop types and their varieties</p>
        </div>
        <CreateCropTypeDialog />
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search crop or variety..."
          value={searchInput}
          onChange={(e) => handleSearchInput(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Stock %</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cropTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  {search ? "No matching crops or varieties" : "No crop types found"}
                </TableCell>
              </TableRow>
            )}
            {cropTypes.map((ct) => {
              const isExpanded = expandedIds.has(ct.id);
              const varieties = varietiesFor(ct.id);

              return (
                <Fragment key={ct.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => toggle(ct.id)}
                  >
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-6 w-6 pointer-events-none"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{ct.code}</TableCell>
                    <TableCell className="font-semibold">{ct.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                      {ct.description || "—"}
                    </TableCell>
                    <TableCell>
                      {ct.stockPercent != null ? (
                        <Badge variant="outline">{ct.stockPercent}%</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">70% (default)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ct.isActive ? "default" : "destructive"}>
                        {ct.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem asChild>
                            <EditCropTypeDialog cropType={ct} />
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteCropType(ct.id, ct.name)}
                            disabled={deleteCropType.isPending}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/20 p-0">
                        <div className="px-8 py-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-muted-foreground">
                              Varieties ({varieties.length})
                            </span>
                            <div onClick={(e) => e.stopPropagation()}>
                              <CreateCropVarietyDialog defaultCropTypeId={ct.id} />
                            </div>
                          </div>

                          {varieties.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-2">
                              No varieties yet. Add one above.
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Days to Transplant</TableHead>
                                  <TableHead>Stock %</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {varieties.map((v) => {
                                  const resolvedPct =
                                    v.effectiveStockPercent ?? ct.stockPercent ?? 70;
                                  const isOverride = v.stockPercent != null;
                                  return (
                                  <TableRow key={v.id}>
                                    <TableCell className="font-medium">{v.name}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {v.sowingToTransplantReadyDays
                                        ? `${v.sowingToTransplantReadyDays}d`
                                        : "—"}
                                    </TableCell>
                                    <TableCell>
                                      {isOverride ? (
                                        <Badge variant="outline">{resolvedPct}%</Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">
                                          {resolvedPct}% (inherited)
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={v.isActive ? "default" : "destructive"}
                                      >
                                        {v.isActive ? "Active" : "Inactive"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="sm">
                                            <MoreHorizontal className="w-4 h-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                          <DropdownMenuItem asChild>
                                            <EditCropVarietyDialog cropVariety={v} />
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            className="text-red-600"
                                            onClick={() =>
                                              handleDeleteVariety(v.id, v.name)
                                            }
                                            disabled={deleteCropVariety.isPending}
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </TableCell>
                                  </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {data && data.totalCount > 0 && (
        <PaginationBar
          currentPage={data.pageNumber}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          onPageChange={setPageNumber}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
};
